import { IDetailsRowProps, IRenderFunction, Stack } from "@fluentui/react";
import React, { ReactElement, useEffect, useState } from "react";

import { Text } from "../items/Text";
import { LayoutMode } from "./LayoutMode";
import { ListTableColumn } from "./ListTableColumn";
import { ListTableSort, sortColumns } from "./ListTableSort";
import { ShimmeredDetailsList } from "./ShimmeredDetailsList";

export type DetailsRowProps = IDetailsRowProps;
export type RenderFunction<T> = IRenderFunction<T>;

export interface ListTableProps<T> {
    columns: ListTableColumn<T>[];
    items: T[];
    isLoading: boolean;
    noItemsMessage?: string;
    sort?: ListTableSort;
    layoutMode?: LayoutMode;
    shimmerLines?: number;
    onRenderRow?: RenderFunction<DetailsRowProps>;
    onRenderExpandedRow?: RenderFunction<DetailsRowProps>;
    onSortChange?(sort: ListTableSort): void;
    onSelectionChange?(selectedItems: T[]): void;
    isItemSelectable?(item: T): boolean;
}

export const DetailsListTable = <T,>({
    columns,
    items,
    isLoading,
    noItemsMessage,
    sort,
    onRenderRow,
    onSortChange,
    onSelectionChange,
    isItemSelectable
}: ListTableProps<T>): ReactElement | null => {
    const [sortedColumns, setSortedColumns] = useState<ListTableColumn<T>[]>(columns);

    useEffect(() => {
        const newColumns = sortColumns(columns, sort);
        setSortedColumns(newColumns);
    }, [sort, columns]);

    const onColumnHeaderClick = (e?: React.MouseEvent<HTMLElement>, column?: ListTableColumn<unknown>) => {
        e?.preventDefault();
        if (column && column.isSortable && onSortChange) {
            if (column.isSorted && !column.isSortedDescending) {
                onSortChange({ by: column.key, order: "desc" });
            } else {
                onSortChange({ by: column.key, order: "asc" });
            }
        }
    };

    return (
        <Stack tokens={{ childrenGap: 15 }}>
            <ShimmeredDetailsList
                columns={sortedColumns}
                items={items}
                enableShimmer={isLoading}
                onColumnHeaderClick={onColumnHeaderClick}
                onRenderRow={onRenderRow}
                onSelectionChange={onSelectionChange}
                isItemSelectable={isItemSelectable}
            />
            {!items.length && !isLoading && (
                <Stack horizontalAlign="center" data-testid="table-no-data">
                    <Text>{noItemsMessage || "No data available"}</Text>
                </Stack>
            )}
        </Stack>
    );
};
